/* eslint-disable react-refresh/only-export-components */
/**
 * Theme Context
 * Manages light/dark theme with MUI theming, and the MUI localisation that
 * follows the interface language (built-in component texts, pagination,
 * date pickers).
 */

import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getBaseLanguage } from '../i18n';
import { getMuiLocalization } from '../i18n/muiLocale';
import {
  brandColors,
  lightBackground,
  darkBackground,
  lightText,
  darkText,
  darkModePrimary,
  darkModeSecondary,
  darkModeAccent,
} from '../config/colors';

import type { PaletteMode, ThemeOptions } from '@mui/material';
import type { ReactNode } from 'react';


interface ThemeContextType {
  mode: PaletteMode;
  toggleTheme: () => void;
  setMode: (mode: PaletteMode) => void;
  colors: typeof brandColors;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const STORAGE_KEY = 'bnft-theme-mode';

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<PaletteMode>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
    return 'light';
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  const setMode = (newMode: PaletteMode) => {
    setModeState(newMode);
  };

  const toggleTheme = () => {
    setModeState((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  const { t, i18n } = useTranslation();
  const language = getBaseLanguage(i18n.language);
  const localization = getMuiLocalization(language);

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: mode === 'light' ? brandColors.primary : darkModePrimary,
          secondary: mode === 'light' ? brandColors.secondary : darkModeSecondary,
          error: mode === 'light'
            ? {
                main: brandColors.accent.main,
                light: brandColors.accent.light,
                dark: brandColors.accent.dark,
                contrastText: brandColors.accent.contrastText,
              }
            : darkModeAccent,
          background: mode === 'light' ? lightBackground : darkBackground,
          text: mode === 'light' ? lightText : darkText,
        },
        typography: {
          fontFamily: [
            'Inter',
            '-apple-system',
            'BlinkMacSystemFont',
            '"Segoe UI"',
            'Roboto',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
            '"Apple Color Emoji"',
            '"Segoe UI Emoji"',
            '"Segoe UI Symbol"',
          ].join(','),
          h1: {
            fontWeight: 700,
            letterSpacing: '-0.02em',
          },
          h2: {
            fontWeight: 700,
            letterSpacing: '-0.01em',
          },
          h3: {
            fontWeight: 600,
          },
          h4: {
            fontWeight: 600,
          },
          h5: {
            fontWeight: 600,
          },
          h6: {
            fontWeight: 600,
          },
        },
        shape: {
          borderRadius: 8,
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 8,
                padding: '10px 24px',
              },
              containedPrimary: mode === 'light'
                ? {
                    '&:hover': {
                      backgroundColor: brandColors.primary.light,
                    },
                  }
                : {
                    backgroundColor: darkModePrimary.main,
                    color: darkModePrimary.contrastText,
                    '&:hover': {
                      backgroundColor: darkModePrimary.light,
                    },
                  },
              containedSecondary: mode === 'light'
                ? {
                    backgroundColor: brandColors.secondary.main,
                    color: brandColors.secondary.contrastText,
                    '&:hover': {
                      backgroundColor: brandColors.secondary.light,
                    },
                  }
                : {
                    backgroundColor: darkModeSecondary.main,
                    color: darkModeSecondary.contrastText,
                    '&:hover': {
                      backgroundColor: darkModeSecondary.light,
                    },
                  },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                boxShadow:
                  mode === 'light'
                    ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                    : '0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -1px rgba(0, 0, 0, 0.3)',
                borderRadius: 12,
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
              },
            },
          },
        },
      },
      // Built-in texts of the MUI components (autocomplete, alerts...)
      localization.core,
      // Every table pagination of the application, worded by our own
      // translations. Pages must not set these labels themselves.
      {
        components: {
          MuiTablePagination: {
            defaultProps: {
              labelRowsPerPage: t('common.rowsPerPage'),
              labelDisplayedRows: ({ from, to, count }) =>
                count === -1
                  ? t('common.pagination.displayedRowsMore', { from, to })
                  : t('common.pagination.displayedRows', { from, to, count }),
              getItemAriaLabel: (type) => t(`common.pagination.${type}Page`),
            },
          },
        },
      } satisfies ThemeOptions),
    [mode, localization, t]
  );

  const effectiveColors = useMemo(
    () =>
      mode === 'light'
        ? brandColors
        : {
            primary: darkModePrimary,
            secondary: darkModeSecondary,
            accent: darkModeAccent,
          },
    [mode]
  );

  const contextValue = useMemo(
    () => ({
      mode,
      toggleTheme,
      setMode,
      colors: effectiveColors,
    }),
    [mode, effectiveColors]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={localization.dateFns}
          localeText={localization.pickersText}
        >
          {children}
        </LocalizationProvider>
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export { ThemeContext };
